/**
 * T-226: at sign-in, fill in the browser's time zone for the person, so their alert
 * summary arrives at 6 PM their time. Once per person per page load. It reads first
 * and writes only when none is set, and the write itself is only_if_unset, so a
 * time zone they chose on Profile is never changed and someone who has one causes
 * no write at all. Failures are quiet; the server uses Pacific until it works.
 */
import { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getMyTimeZone, saveMyTimeZone } from '@/lib/alertDigestApi';
import { announceTimeZoneSaved } from '@/lib/timeZoneEvents';

const filled = new Set<string>();

/** Test hook only: forget which people were filled on this page load. */
export function resetTimeZonePrefillForTests(): void {
  filled.clear();
}

function browserTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

async function fill(zone: string): Promise<void> {
  const current = await getMyTimeZone();
  if (current.time_zone) return;
  const saved = await saveMyTimeZone(zone, { onlyIfUnset: true });
  // An open Profile page that read "not set" shows it without a reload.
  if (saved?.time_zone) announceTimeZoneSaved(saved.time_zone);
}

export function useTimeZonePrefill(): void {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  useEffect(() => {
    if (!userId || filled.has(userId)) return;
    const zone = browserTimeZone();
    if (!zone) return;
    filled.add(userId);
    // Quiet either way (a rejection or a throw): nothing here may break the provider that runs it.
    Promise.resolve().then(() => fill(zone)).catch(() => {});
  }, [userId]);
}
